import React from 'react';
import { Box, Text, Tooltip } from '@wix/design-system';
import { PricingStrategy } from '@wix/ambassador-premium-domains-v1-recommendation/types';
import { GiftSmall, InfoCircleSmall } from '@wix/wix-ui-icons-common/odeditor';
import { getPremiumEssentialsSafe } from '@wix/premium-fed-sdk/@premium-essentials';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import { useIsMobile } from '@wix/core-domain-search';
import { parseDomain } from '@wix/domains-serverless-interface';
import { PricingTooltip } from './PricingTooltip';
import { DomainSearchSuiteModule } from '../../module/domainSearchSuite.module';
import { ModalsModule } from '../../module/extensions/ModalsExtensions';
import { ExperimentSpecs } from '../../constants';
import commonStyles from '../common.scss';
import styles from './Pricing.module.scss';
import { DomainWithEllipsis } from '../DomainWithEllipsis';
const PRICING_STRATEGY_MAPPING = {
  [PricingStrategy.FREE_FIRST_YEAR]: {
    labelKey: 'domain_search_suite.domain_gate.suggestions.card.one_year_free',
    tooltipKey: 'domain_search_suite.domain_gate.suggestions.card.one_year_free.tooltip',
    voucherTooltipKey: 'domain_search_gate.results.free_for_one_year.use_voucher.tooltip'
  },
  [PricingStrategy.RENEWAL]: {
    labelKey: 'domain_search_suite.business_email_results.intro_price.label',
    tooltipKey: 'domain_search_suite.results.intro_price.tooltip'
  },
  [PricingStrategy.MULTI_YEAR_MINIMUM_COUNT]: {
    labelKey: 'domain_search_suite.business_email_results.intro_price.label',
    tooltipKey: 'domain_search_suite.results.intro_price.tooltip'
  }
};
const PRICING_STRATEGY_MAPPING_MATCH_CYCLE = {
  [PricingStrategy.FREE_FIRST_YEAR]: {
    labelKey: 'domain_search_suite.domain_gate.suggestions.card.match_cycle',
    tooltipKey: 'domain_search_suite.domain_gate.suggestions.card.match_cycle.tooltip',
    voucherTooltipKey: 'domain_search_gate.results.free_for_one_year.use_voucher.match_cycle.tooltip'
  }
};
export const PricingStrategyLabel = _ref => {
  var _getPremiumEssentials, _getPremiumEssentials2;
  let pricing = _ref.pricing,
    domainName = _ref.domainName,
    _ref$useIntroPriceLab = _ref.useIntroPriceLabelCopy,
    useIntroPriceLabelCopy = _ref$useIntroPriceLab === void 0 ? false : _ref$useIntroPriceLab,
    _ref$iconOnly = _ref.iconOnly,
    iconOnly = _ref$iconOnly === void 0 ? false : _ref$iconOnly,
    _ref$textOnly = _ref.textOnly,
    textOnly = _ref$textOnly === void 0 ? false : _ref$textOnly,
    _ref$compactPriceLabe = _ref.compactPriceLabel,
    compactPriceLabel = _ref$compactPriceLabe === void 0 ? false : _ref$compactPriceLabe;
  const _DomainSearchSuiteMod = DomainSearchSuiteModule.internals.useTranslations(),
    t = _DomainSearchSuiteMod.t,
    Trans = _DomainSearchSuiteMod.Trans;
  const _DomainsApiClient$hoo = DomainsApiClient.hooks.useDomainsAccount({}),
    accountData = _DomainsApiClient$hoo.data;
  const isMobile = useIsMobile();
  const isRenewalPricingEnabled = (_getPremiumEssentials = getPremiumEssentialsSafe()) == null || (_getPremiumEssentials = _getPremiumEssentials.experiments) == null ? void 0 : _getPremiumEssentials[ExperimentSpecs.RenewalPricingStrategy];
  const isMatchCycle = !!((_getPremiumEssentials2 = getPremiumEssentialsSafe()) != null && (_getPremiumEssentials2 = _getPremiumEssentials2.experiments) != null && _getPremiumEssentials2[ExperimentSpecs.MatchDomainCycleToPremiumCycle]);
  const isIntroStrategy = pricing.pricingStrategy === PricingStrategy.RENEWAL || pricing.pricingStrategy === PricingStrategy.MULTI_YEAR_MINIMUM_COUNT && pricing.renewalPrice;
  const isMultiYearWithoutRenewal = pricing.pricingStrategy === PricingStrategy.MULTI_YEAR_MINIMUM_COUNT && !pricing.renewalPrice;
  const strategyMap = isMatchCycle && pricing.pricingStrategy === PricingStrategy.FREE_FIRST_YEAR ? PRICING_STRATEGY_MAPPING_MATCH_CYCLE : PRICING_STRATEGY_MAPPING;
  const config = pricing.pricingStrategy && !isMultiYearWithoutRenewal ? strategyMap[pricing.pricingStrategy] : undefined;
  if (!config) {
    return null;
  }
  if (isIntroStrategy && !isRenewalPricingEnabled && !useIntroPriceLabelCopy) {
    return null;
  }
  const isFreeFirstYear = pricing.pricingStrategy === PricingStrategy.FREE_FIRST_YEAR;
  const isVoucherLabel = compactPriceLabel && isFreeFirstYear;
  const labelKey = isVoucherLabel ? 'domains_search_suite.anon_storefront_results.optimizations.initial_vs_renewal.voucher_badge' : useIntroPriceLabelCopy ? 'domains_search_suite.anon_storefront_results.optimizations.container.intro_price.label' : config.labelKey;
  const isAiRenewal = isIntroStrategy && parseDomain(domainName ?? '').tld === 'ai';
  const aiTooltipText = isAiRenewal ? t('domain_search_suite.tld_section.ai_tooltip.text') : undefined;
  const buildAiContent = (baseText, light) => /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP2"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "small",
    light: light
  }, baseText), /*#__PURE__*/React.createElement(Text, {
    size: "small",
    light: light
  }, aiTooltipText));
  const defaultTooltipKey = accountData != null && accountData.hasValidVouchers && config.voucherTooltipKey ? config.voucherTooltipKey : config.tooltipKey;
  const tooltipText = useIntroPriceLabelCopy ? t('domains_search_suite.anon_storefront_results.optimizations.initial_vs_renewal.tooltip.initial_offer', {
    renewalPrice: pricing.renewalPrice
  }) : t(defaultTooltipKey, {
    price: pricing.renewalPrice
  });
  const tooltipContent = isAiRenewal ? buildAiContent(tooltipText, true) : tooltipText;
  const modalContent = isAiRenewal ? buildAiContent(tooltipText, false) : tooltipText;
  const openInfoModal = () => {
    const infoModal = ModalsModule.internals.domainsSharedModalsController.infoModal;
    infoModal.show({
      title: isFreeFirstYear ? /*#__PURE__*/React.createElement(Box, {
        width: "90%"
      }, /*#__PURE__*/React.createElement(Text, {
        size: "medium",
        weight: "bold"
      }, /*#__PURE__*/React.createElement(Trans, {
        i18nKey: isMatchCycle ? 'domain_search_gate.mobile.PriceInfoModal.content.title.match_cycle' : 'domain_search_gate.mobile.PriceInfoModal.content.title',
        values: {
          domain_name: domainName
        },
        components: [/*#__PURE__*/React.createElement("span", null)]
      }))) : /*#__PURE__*/React.createElement(DomainWithEllipsis, {
        maxWidth: "220px",
        textComponentProps: {
          weight: 'bold'
        }
      }, domainName),
      content: /*#__PURE__*/React.createElement(Box, {
        direction: "vertical",
        gap: "SP2"
      }, typeof modalContent === 'string' ? /*#__PURE__*/React.createElement(Text, {
        size: "small",
        weight: "thin"
      }, modalContent) : modalContent)
    });
  };
  return /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    align: "center",
    verticalAlign: "middle",
    gap: "2px"
  }, !iconOnly && /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    verticalAlign: "middle",
    gap: "4px",
    className: isVoucherLabel ? styles.voucherLabel : undefined
  }, isVoucherLabel && /*#__PURE__*/React.createElement(GiftSmall, null), /*#__PURE__*/React.createElement(Text, {
    size: "tiny",
    weight: isVoucherLabel ? 'bold' : 'normal',
    className: useIntroPriceLabelCopy || isVoucherLabel ? undefined : styles.highlightPrice
  }, t(labelKey))), !textOnly && (isMobile ? /*#__PURE__*/React.createElement(InfoCircleSmall, {
    className: commonStyles.infoIcon,
    "data-hook": "pricing-strategy-info-icon",
    onClick: event => {
      event.stopPropagation();
      openInfoModal();
    }
  }) : /*#__PURE__*/React.createElement(Tooltip, {
    maxWidth: "250px",
    content: /*#__PURE__*/React.createElement(PricingTooltip, {
      content: tooltipContent
    }),
    appendTo: "scrollParent",
    zIndex: 999999,
    placement: "bottom"
  }, /*#__PURE__*/React.createElement(Box, {
    verticalAlign: "middle"
  }, /*#__PURE__*/React.createElement(InfoCircleSmall, {
    className: commonStyles.infoIcon,
    "data-hook": "pricing-strategy-info-icon"
  })))));
};
//# sourceMappingURL=PricingStrategyLabels.js.map